import { flushSync } from 'react-dom';
import React, {
  createContext,
  useContext,
  useState,
  // useEffect
} from 'react';
import modules from 'modules/index';
import getConfig from 'next/config';
import { debounce, toArray } from 'utils/functions';
import {
  doMutation,
  // doQuery
} from 'utils/graphql';
import { upsertUserSettings } from './mutations';
// import { userSettings } from './queries';

const { publicRuntimeConfig } = getConfig();

const isDev = publicRuntimeConfig.ENV_TYPE === 'DEV';

const WindowsContext = createContext();
let currentZIndex = 0;

function WindowsProvider ({ children }) {
  // whatever you set here as default, will be used in both server side and client as well

  const allAvailable = modules.available;
  const modsAvailable =  isDev ? allAvailable : {};

  if (!isDev) {
    // if env is not in development mode, we will hide some modules.
    // Some of the modules may actually be in hml
    for (let modId in allAvailable) {
      // remove all components in category dev
      // if (
      //   allAvailable[modId]?.category?.toLowerCase() === 'dev'
      // ) { continue; }
      modsAvailable[modId] = allAvailable[modId];
    }
  }

  const [providerData, setProviderData] = useState({
    ready: false,
    data: {
      // add default data to your provider here
      active: null,
      open: [],
      recent: [],
      // ...storedState,
      available: modsAvailable
    }
  });

  // you can get extra data asynchronously if needed
  // this will only affect the client side
  async function fetchProviderData () {
    const recentHistory = JSON.parse(
      localStorage.getItem('recentHistory') || '[]'
    );

    setProviderData({
      ...providerData,
      recent: recentHistory
    });
  }

  React.useEffect(() => { fetchProviderData(); }, []);

  const value = {
    ...providerData,
    setData (key, value) {
      const newData = {
        ...providerData,
        data: {
          ...providerData.data,
          [key]: value
        }
      };
      setProviderData(newData);
    }
  };

  value.setFocus = function (id) {
    setProviderData(curData => {
      const newData = {...curData};
      newData.data.open = curData.data.open.map(w => {
        if (w.id === id) {
          w.minimized = false;
          currentZIndex++;
          w.order = currentZIndex;
        }
        return w;
      });
      newData.data.active = id;
      return newData;
    });
  };

  React.useEffect(_ => {
    function keydownListener (event) {
      // if (event.key === "Escape" && event.ctrlKey) {
      if (event.key === "Escape" && event.altKey) {
        setProviderData(curData => {
          const newData = {...curData};
          newData.data.debug = true;
          return newData;
        });
      }
    }

    function keyUpListener (event) {
      if (event.key === "Escape") {
        setProviderData(curData => {
          const newData = {...curData};
          newData.data.debug = false;
          return newData;
        });
      }
    }

    if (isDev) {
      document.body.addEventListener('keydown', keydownListener);
      document.body.addEventListener('keyup', keyUpListener);

      return _ => {
        document.body.removeEventListener('keydown', keydownListener);
        document.body.removeEventListener('keyup', keyUpListener);
      };
    }
  }, []);

  function getWindowById (id, list) {
    list = list || value;
    return list.data.open.find(item => item.id === id) || null;
  }

  value.minimizeWindow = function (id) {
    setProviderData(curData => {
      const unminimized = [];
      const newOpenList = curData.data.open.map(w => {
        if (w.id === id) {
          w.minimized = true;
        } else if (!w.minimized) {
          unminimized.push(w);
        }
        return w;
      });

      let nextWinToFocus = null;
      unminimized.forEach(win => {
        if (win.id === id) { return; }
        if (!nextWinToFocus || nextWinToFocus.order < win.order) {
          nextWinToFocus = win;
        }
      });

      nextWinToFocus = nextWinToFocus?.id || null;

      const newData = {...curData};
      newData.data.open = newOpenList;
      newData.data.active = nextWinToFocus;
      return newData;
    });
    // value.setData('open', newOpenList);
    // value.setData('active', nextWinToFocus);
  };

  value.closeAllWindows = function (areYouSure = false, options = {}) {
    if (!areYouSure) {
      return;
    }

    const keep = toArray(options.keep || []);

    setProviderData(curData => {
      const newData = {...curData};
      let currentOpen = curData.data.open.filter(item => {
        if (keep.includes(item.id)) {
          return true;
        }
        return false;
      });
      newData.data.open = currentOpen || [];
      newData.data.active = options?.keep?.[0] || null;
      return newData;
    });
  };

  value.closeWindow = function (id) {
    setProviderData(curData => {
      const newData = {...curData};
      let spawned = null;
      let nextActive = null;
      let currentOpen = curData.data.open.filter(item => {
        if (item.id === id) {
          spawned = item.spawned;

          if (item.parent) {
            const parentWindow = getWindowById(item.parent, curData);
            if (parentWindow?.tiedTo === item.id) {
              // changing by reference
              // will untie parent window
              parentWindow.tiedTo = null;
              nextActive = parentWindow;
            }
          }
          return false;
        }
        return true;
      });

      if (spawned && spawned.length) {
        // closes all child windows with it
        currentOpen = currentOpen.filter(item => {
          // if (item.order > (biggerOrder.order || 0)) {
          //   biggerOrder = item;
          // }
          return !spawned.includes(item.id);
        });
      }

      // find the next window to be focused
      if (nextActive) {
        currentZIndex++;
        nextActive.order = currentZIndex;
      } else {
        currentOpen.forEach(item => {
          if (item.order > (nextActive?.order || 0)) {
            nextActive = item;
          }
        });
      }

      // if (!nextActive && currentOpen.length) {
      //   nextActive = biggerOrder?.id || null;
      // }

      newData.data.open = currentOpen;
      newData.data.active = nextActive?.id || null;
      return newData;
    });
  };

  value.saveCurrentWindows = async function saveCurrentWindows () {
    const wins = [];
    providerData.data.open.forEach(openW => {
      const pos = openW.props.apis.window.getPosition();
      const size = openW.props.apis.window.getSize();
      if (pos) {
        openW.windowOptions = {
          ...openW.windowOptions,
          ...pos,
          ...size
        }; // update with current window's position and size
        wins.push(openW);
      }
    });

    const newData = {
      data: {
        workspaces: {
          active: providerData.data.active,
          open: wins
        }
      }
    };

    sendCurrentWindows(newData);
  };

  const sendCurrentWindows = debounce(async (newData) => {
    await doMutation(upsertUserSettings, newData);
  },500);

  value.openWindow = function openWindow (type, props = {}, options = {}) {
    return new Promise((resolve, reject) => {
      setProviderData(curData => {
        const windowToOpen = curData.data.available[type];

        const {
          windowOptions:optsWindowOptions,
          ...otherOptions
        } = options;

        const windowOptions = {
          ...(windowToOpen?.windowOptions || {}),
          ...(optsWindowOptions || {}),
          ...otherOptions
        };

        if (!windowToOpen) {
          const msg = `[W_ERR] :: Invalid module type "${type}"`;
          reject(msg);
          console.error(msg);
          return curData;
        }

        const {
          title:overwritenTitle,
          icon:overwritenIcon,
          overwritenWOptions,
        } = options;

        if (windowToOpen.allowIconOverwride && overwritenIcon) {
          windowToOpen.icon = overwritenIcon;
        }

        if (windowToOpen.allowTitleOverwride && overwritenTitle) {
          windowToOpen.title = overwritenTitle;
        }

        if (windowToOpen.allowWindowOptionsOverwride && overwritenWOptions) {
          windowToOpen.windowOptions = overwritenWOptions;
        }

        let id = null;
        const currentOpen = curData.data.open;
        const hasInstances = curData.data.open.filter(
          item => item.type === type
        );

        if (windowToOpen.unique && hasInstances.length) {
          // only focus the last one
          id = hasInstances[0].id;
          hasInstances[0].order = currentZIndex;
        } else {
          // actually opens a window
          id = currentOpen.length + "" + (new Date()).getTime();
        }

        currentZIndex++;
        if (windowToOpen.unique && hasInstances.length) {
          // only focus the last one
          id = hasInstances[0].id;
          hasInstances[0].order = currentZIndex;
          resolve(id);
        } else {
          // actually opens a window
          id = currentOpen.length + "" + (new Date()).getTime();

          props.apis = {
            window: {
              id,
              spawn (...args) {
                return spawn(id, ...args);
              },
              open (...args) {
                return open(id, ...args);
              },
              navigate (type,
                data = {},
                options = {
                  resize: true, isMovingBack: false, isMovingForward: false
                }
              ) {
                // flushSync(_ => {
                setProviderData(curData => {
                  const windowToOpen = curData.data.available[type];

                  if (!windowToOpen) {
                    console.error(`[win] :: Could not find a ${type} module to navigate to`);
                    return;
                  }

                  const newData = {...curData};
                  const currentOpen = curData.data.open;
                  const theWindow = currentOpen.find(openW => openW.id === id);
                  theWindow.type = type;
                  theWindow.fileName = windowToOpen.fileName;

                  if (options.resize) {
                    const curWindowAPI = theWindow.props.apis.window;
                    if (!curWindowAPI.getDOMElement()?.dataset.isMax) {
                      // const {
                      //   x:prevWinX,
                      //   y:prevWinY,
                      //   width: prevWinW
                      // } = curWindowAPI.getDimensions();
                      // const {
                      //   x: nextWinX = prevWinX,
                      //   width: nextWinW = prevWinW
                      // } = windowToOpen.windowOptions;

                      // const adjustedX = Math.round(
                      //   nextWinX + (
                      //     Math.max(prevWinW, nextWinW) -
                      //     Math.min(prevWinW, nextWinW)
                      //     / 2)
                      // );

                      // windowToOpen.windowOptions.x = adjustedX;
                      // windowToOpen.windowOptions.y = prevWinY;

                      const prevWindowOptions = {
                        ...(theWindow.windowOptions || {})
                      };
                      theWindow.windowOptions = {
                        ...prevWindowOptions,
                        ...(windowToOpen.windowOptions || {}),
                      };
                    }
                  }

                  theWindow.history = theWindow.history || new WindowHistory();
                  if (!options.isMovingBack && !options.isMovingForward) {
                    theWindow.history.push(
                      type,
                      {
                        ...(windowToOpen.props || {}),
                        ...data,
                        windowOptions: windowToOpen.windowOptions
                      }
                    );
                  }

                  theWindow.props = {
                    history: theWindow.history,
                    loading: true,
                    apis: props.apis,
                    ...(windowToOpen.props || {}),
                    ...data
                  };

                  newData.data.open = [...currentOpen];
                  return newData;
                });
                // });
              }
            },
            ui: {}
          };

          currentOpen.push({
            id,
            type,
            fileName: windowToOpen.fileName || type,
            minimized: false,
            order: currentZIndex,
            props,
            windowOptions,
            history: new WindowHistory(
              type,
              {
                ...(windowToOpen.props || {}),
                ...props,
                windowOptions
              }
            ),
            spawned: [],
            tiedTo: null,
            ...otherOptions
          });
        }

        const newData = {...curData};
        newData.data.open = currentOpen;
        newData.data.active = id;
        setTimeout(() => {
          resolve(id);
        }, 10);

        // ensuring it will not be added twice to the list of recent
        newData.recent = newData.recent.filter(item => item.id !== type);
        const { apis:_, ...usedProps } = props;
        // add as the most recent item
        // debugger; console.log(props, options);
        newData.recent.unshift({
          id: newData.data.available[type].id,
          props: usedProps,
          title: (
            props.moduleActionTitle ||
            newData.data.available[type].title
          )
        });
        newData.recent = newData.recent.slice(0, 5);

        new Promise((resolve, reject) => {
          try {
            localStorage
              .setItem(
                'recentHistory',
                JSON.stringify(newData.recent)
              );
            resolve();
          } catch (error) {
            reject();
          }
        });

        return newData;
      });
      // value.setData('open', currentOpen);
      // value.setData('active', id);
    });
  };

  async function open (id, type, props = {}, options = {}) {
    return await value.openWindow(
      type,
      props,
      { parent: id, ...options}
    );
  }

  async function spawn (id, type, props = {}, options = {}) {
    const createdId = await value.openWindow(
      type,
      props,
      { parent: id, ...options}
    );

    setProviderData(curData => {
      const newData = {...curData};
      const parentIdx = newData.data.open.findIndex(i => i.id === id);
      newData.data.open[parentIdx].spawned.push(createdId);

      if (options.tied) {
        newData.data.open[parentIdx].tiedTo = createdId;
      }

      return newData;
    });
  }

  value.spawn = spawn;

  return (
    <WindowsContext.Provider value={value}>
      {children}
    </WindowsContext.Provider>
  );
}

// this allows you to use it as a simpler hook
function useWindows () {
  const context = useContext(WindowsContext);
  if (context === undefined) {
    throw new Error('useWindows must be used within a WindowsProvider');
  }

  return context;
}

export {
  WindowsProvider,
  WindowsContext,
  useWindows
};

function WindowHistory (firstItem, firstData) {
  const histList = [];
  let cursor = -1;

  this.push = (item, data) => {
    histList.splice(cursor + 1); // will replace the forward history
    histList.push({item, data});
    cursor = histList.length - 1;
    return histList[cursor];
  };

  this.back = (otherProps = {}) => {
    if (cursor === -1) {
      return false;
    }
    cursor--;
    const ret = histList[cursor];
    return [ret.item, {...ret.data, ...otherProps}];
  };

  this.forward = () => {
    if (cursor === histList.length - 1) {
      return false;
    }
    cursor++;
    const ret = histList[cursor];
    return [ret.item, ret.data];
  };

  this.canBack = function () {
    return !!histList[cursor];
  };

  this.canForward = function () {
    return !!histList[cursor + 1];
  };

  if (firstItem) {
    this.push(firstItem, firstData);
  }

  return this;
}
